/**
 * Iframe-based print utility for Healist.AI
 * Creates a hidden iframe, writes content, triggers browser print dialog.
 * No new tabs, no CSS visibility hacking, works in PWAs.
 */

function escapeHTML(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function printHTML(html, title = 'Print') {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(`<!DOCTYPE html><html>
<head>
  <title>${escapeHTML(title)}</title>
  <style>
    body {
      font-family: 'Outfit', Arial, sans-serif;
      padding: 24px;
      color: #1f2937;
      line-height: 1.6;
    }
    h1 { color: #1E3A5F; font-size: 22px; margin: 0 0 4px; }
    h2 { color: #1E3A5F; font-size: 16px; margin: 16px 0 8px; border-bottom: 2px solid #C9A962; padding-bottom: 4px; }
    h3 { color: #1E3A5F; font-size: 14px; margin: 12px 0 6px; }
    p { margin: 4px 0; }
    pre {
      white-space: pre-wrap;
      word-wrap: break-word;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      line-height: 1.5;
      background: #f9fafb;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }
    .meta { color: #6b7280; font-size: 13px; }
    .item-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-bottom: 1px solid #f0f0f0; }
    .checkbox { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #1E3A5F; border-radius: 2px; flex-shrink: 0; }
    .brand-line { height: 3px; background: linear-gradient(90deg, #1E3A5F, #C9A962); margin: 12px 0; border-radius: 2px; }
    .day-card { margin-bottom: 12px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
    .day-header { padding: 8px 12px; font-weight: 600; font-size: 14px; border-bottom: 1px solid #e5e7eb; }
    .day-content { padding: 8px 12px; font-size: 13px; }
    .exercise-row { padding: 3px 0; }
    .exercise-name { font-weight: 500; }
    .exercise-detail { color: #6b7280; font-size: 12px; margin-left: 8px; }
    .section-label { font-weight: 600; font-size: 12px; color: #1E3A5F; margin: 6px 0 2px; text-transform: uppercase; letter-spacing: 0.5px; }
    .footer { margin-top: 24px; text-align: center; color: #9ca3af; font-size: 11px; border-top: 1px solid #e5e7eb; padding-top: 12px; }
    @page { margin: 0.75in; }
  </style>
</head>
<body>
  ${html}
  <div class="footer">Printed from Healist.AI</div>
</body></html>`);
  doc.close();

  iframe.contentWindow.onafterprint = () => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  };

  // Fallback cleanup
  setTimeout(() => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  }, 60000);

  // Wait for all images to load before printing
  waitForImagesAndPrint(doc, iframe);
}

export function printShoppingList(list) {
  const itemsHtml = (list.items || []).map(item => {
    const name = item.name || item.item_name || '';
    const qty = typeof item.quantity === 'string'
      ? item.quantity
      : `${item.quantity || 1} ${item.unit || ''}`;
    return `<div class="item-row"><span class="checkbox"></span><span>${escapeHTML(name)}</span> <span class="meta">(${escapeHTML(qty.trim())})</span></div>`;
  }).join('');

  printHTML(`
    <h1>${escapeHTML(list.name)}</h1>
    ${list.store_preference ? `<p class="meta">Store: ${escapeHTML(list.store_preference)}</p>` : ''}
    <p class="meta">${list.items?.length || 0} items</p>
    <div class="brand-line"></div>
    ${itemsHtml}
  `, list.name || 'Shopping List');
}

export function printFileContent(content, filename) {
  printHTML(`
    <h1>${escapeHTML(filename)}</h1>
    <div class="brand-line"></div>
    <pre>${escapeHTML(content)}</pre>
  `, filename || 'File');
}

/**
 * Print a full HTML document (e.g. server-rendered recipe page).
 * Writes the complete HTML into a hidden iframe and triggers print.
 * Use this for API-generated HTML pages — NOT for plain text or PDFs.
 */
/**
 * Wait for all <img> elements to load, then print.
 * Falls back after 5s if images haven't loaded.
 */
function waitForImagesAndPrint(doc, iframe) {
  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch { /* iframe may have been removed */ }
  };

  const images = Array.from(doc.querySelectorAll('img'));
  const imagePromises = images.map(img => {
    if (img.complete) return Promise.resolve();
    return new Promise(resolve => {
      img.onload = resolve;
      img.onerror = resolve;
    });
  });

  if (images.length === 0) {
    doPrint();
  } else {
    Promise.all(imagePromises).then(() => {
      // Small delay for CSS background-image rendering
      setTimeout(doPrint, 300);
    });
    // Fallback: print after 5s even if images haven't loaded
    setTimeout(doPrint, 5000);
  }
}

export function printHTMLDocument(htmlDocument, title = 'Print') {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(htmlDocument);
  doc.close();

  iframe.contentWindow.onafterprint = () => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  };

  setTimeout(() => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  }, 60000);

  // Wait for all images to load before printing
  waitForImagesAndPrint(doc, iframe);
}

/**
 * Consolidated print dispatcher — routes to the correct print method.
 *
 * contentType guide:
 *   'pdf'        → Cannot print PDFs via JS; caller should show PDF toolbar hint
 *   'html'       → Full HTML document rendered in hidden iframe (printHTMLDocument)
 *   'text'       → Plain text escaped into <pre> block (printFileContent)
 */
export function printContent(content, filename, contentType = 'text') {
  const type = (contentType || 'text').toLowerCase();
  if (type === 'html' || type === 'text/html') {
    printHTMLDocument(content, filename);
  } else {
    // Plain text, csv, markdown, etc. — escape and wrap in <pre>
    printFileContent(content, filename);
  }
}

/**
 * Format a single ingredient (handles both dict and string formats — CLAUDE.md lesson #21).
 */
function formatIngredient(ing) {
  if (typeof ing === 'string') return escapeHTML(ing);
  if (typeof ing === 'object' && ing !== null) {
    const qty = ing.quantity || '';
    const unit = ing.unit || '';
    const name = ing.name || ing.ingredient || '';
    return escapeHTML(`${qty} ${unit} ${name}`.trim());
  }
  return escapeHTML(String(ing));
}

/**
 * Get source badge label for a recipe.
 */
function recipeSourceBadge(recipe) {
  if (recipe.ai_generated || recipe.recipe_source === 'ai_generated') return '\ud83e\udde0 AI';
  if (recipe.recipe_source === 'uploaded' || recipe.file_path) return '\ud83d\udcc4 Upload';
  return '\u270d\ufe0f Manual';
}

/**
 * Build HTML for a single recipe card (used by both printRecipeCards and printRecipeBook).
 */
function buildRecipeCardHTML(recipe, opts = {}) {
  const { showImage = true, bookMode = false } = opts;
  const ingredients = recipe.ingredients || [];
  const instructions = recipe.instructions || '';
  const steps = typeof instructions === 'string'
    ? instructions.split('\n').filter(s => s.trim())
    : Array.isArray(instructions) ? instructions : [String(instructions)];

  const imgHtml = (showImage && recipe.image_url)
    ? `<div style="text-align:center;margin-bottom:12px"><img src="${escapeHTML(recipe.image_url)}" alt="${escapeHTML(recipe.title)}" style="max-width:100%;max-height:${bookMode ? '300px' : '240px'};border-radius:8px;object-fit:cover" /></div>`
    : '';

  const metaItems = [];
  if (recipe.servings) metaItems.push(`Serves ${recipe.servings}`);
  if (recipe.prep_time_minutes) metaItems.push(`Prep: ${recipe.prep_time_minutes} min`);
  if (recipe.cook_time_minutes) metaItems.push(`Cook: ${recipe.cook_time_minutes} min`);
  const metaHtml = metaItems.length
    ? `<p class="meta">${metaItems.join(' &bull; ')}</p>`
    : '';

  const ingredientsHtml = ingredients.length
    ? `<h2>Ingredients</h2><ul>${ingredients.map(i => `<li>${formatIngredient(i)}</li>`).join('')}</ul>`
    : '';

  const stepsHtml = steps.length
    ? `<h2>Instructions</h2><ol>${steps.map(s => `<li>${escapeHTML(s.replace(/^\d+[\.\)]\s*/, ''))}</li>`).join('')}</ol>`
    : '';

  // Nutrition grid
  const cal = recipe.calories_per_serving || recipe.full_nutrients?.ENERC_KCAL?.quantity;
  const prot = recipe.protein_grams || recipe.full_nutrients?.PROCNT?.quantity;
  const carb = recipe.carb_grams || recipe.full_nutrients?.CHOCDF?.quantity;
  const fat = recipe.fat_grams || recipe.full_nutrients?.FAT?.quantity;
  const hasNutrition = cal || prot || carb || fat;
  const nutritionHtml = hasNutrition
    ? `<h2>Nutrition (per serving)</h2>
       <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:8px">
         <tr style="background:#f0f4f8;text-align:center">
           <td style="padding:6px;border:1px solid #e5e7eb"><strong>Calories</strong><br/>${cal ? Math.round(cal) : '—'}</td>
           <td style="padding:6px;border:1px solid #e5e7eb"><strong>Protein</strong><br/>${prot ? Math.round(prot) + 'g' : '—'}</td>
           <td style="padding:6px;border:1px solid #e5e7eb"><strong>Carbs</strong><br/>${carb ? Math.round(carb) + 'g' : '—'}</td>
           <td style="padding:6px;border:1px solid #e5e7eb"><strong>Fat</strong><br/>${fat ? Math.round(fat) + 'g' : '—'}</td>
         </tr>
       </table>`
    : '';

  // Health / diet labels
  const labels = [
    ...(recipe.health_labels || []),
    ...(recipe.diet_labels || [])
  ];
  const labelsHtml = labels.length
    ? `<div style="margin-top:6px">${labels.map(l => `<span style="display:inline-block;background:#f0f4f8;border-radius:12px;padding:2px 8px;font-size:11px;margin:2px 3px;color:#1E3A5F">${escapeHTML(l)}</span>`).join('')}</div>`
    : '';

  return `
    <div style="page-break-inside:avoid;${!bookMode ? 'page-break-after:always;' : ''}">
      ${imgHtml}
      <h1>${escapeHTML(recipe.title)} <span style="font-size:13px;font-weight:400;color:#6b7280;margin-left:8px">${recipeSourceBadge(recipe)}</span></h1>
      ${recipe.description ? `<p style="color:#6b7280;font-size:13px;margin:0 0 8px">${escapeHTML(recipe.description)}</p>` : ''}
      ${metaHtml}
      <div class="brand-line"></div>
      ${ingredientsHtml}
      ${stepsHtml}
      ${nutritionHtml}
      ${labelsHtml}
    </div>
  `;
}

/**
 * Print one or more recipe cards. Each gets its own page.
 */
export function printRecipeCards(recipes) {
  if (!recipes || recipes.length === 0) return;
  const today = new Date().toLocaleDateString();
  const cardsHtml = recipes.map(r => buildRecipeCardHTML(r, { showImage: true, bookMode: false })).join('');
  printHTML(`
    ${cardsHtml}
    <div class="footer" style="page-break-before:avoid">Printed from Healist.AI &mdash; ${escapeHTML(today)}</div>
  `, 'Recipe Cards');
}

/**
 * Build full Recipe Book HTML document: cover, TOC, recipe pages, notes, back cover.
 * Returns the HTML string — does NOT print. Use printHTMLDocument() to print.
 */
export function buildRecipeBookHTML(recipes, userName, coverUrl) {
  if (!recipes || recipes.length === 0) return '';
  const today = new Date().toLocaleDateString();
  const displayName = userName || 'My';

  // Cover page — matches approved prototype: background image + gradient overlay, logo + title side-by-side
  const coverBgImg = coverUrl
    ? `background-image:url('${escapeHTML(coverUrl)}');background-size:cover;background-position:center;`
    : `background:linear-gradient(135deg, #1E3A5F 0%, #2a4f7f 50%, #C9A962 100%);`;
  // Logo is the PWA icon at /icon-384x384.png — use absolute URL from window.location
  const logoUrl = `${window.location.origin}/icon-384x384.png`;
  const coverPage = `
    <div style="${coverBgImg}width:100%;min-height:100vh;display:flex;flex-direction:column;align-items:center;position:relative;page-break-after:always">
      <!-- Gradient overlay for readability -->
      <div style="position:absolute;top:0;left:0;right:0;bottom:0;background:linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.55) 40%, rgba(255,255,255,0.3) 100%)"></div>
      <!-- Hero row: logo + title block side-by-side -->
      <div style="position:relative;z-index:1;display:flex;align-items:center;justify-content:space-evenly;width:100%;padding:80px 36px 0">
        <img src="${escapeHTML(logoUrl)}" alt="Healist.AI" style="width:140px;height:140px;filter:drop-shadow(20px 20px 10px rgba(0,0,0,0.35))" />
        <div style="text-align:center">
          <div style="font-family:'Cormorant Garamond',Georgia,serif;font-size:34px;font-weight:700;color:#1A1A2E;line-height:1.15">${escapeHTML(displayName)}'s</div>
          <div style="font-family:'Cormorant Garamond',Georgia,serif;font-size:34px;font-weight:700;color:#1A1A2E;line-height:1.15;margin-top:4px">Recipe Book</div>
        </div>
      </div>
      <!-- Date at bottom center -->
      <div style="position:absolute;bottom:36px;left:0;right:0;text-align:center;font-family:'Cormorant Garamond',Georgia,serif;font-size:22.5px;font-weight:700;color:#1A1A2E;z-index:1;text-shadow:0 1px 4px rgba(255,255,255,0.8)">${escapeHTML(today)}</div>
    </div>
  `;

  // Table of Contents
  const tocItems = recipes.map((r, i) => `<div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px dotted #e5e7eb"><span>${i + 1}. ${escapeHTML(r.title)}</span><span style="color:#6b7280">${i + 2}</span></div>`).join('');
  const tocPage = `
    <div style="page-break-after:always">
      <h1 style="text-align:center;color:#1E3A5F;font-family:'Cormorant Garamond',Georgia,serif">Table of Contents</h1>
      <div style="max-width:500px;margin:0 auto">${tocItems}</div>
    </div>
  `;

  // Recipe pages
  const recipePages = recipes.map(r =>
    `<div style="page-break-after:always">${buildRecipeCardHTML(r, { showImage: true, bookMode: true })}</div>`
  ).join('');

  // Notes pages (2 lined pages)
  const lineCount = 28;
  const lines = Array(lineCount).fill('<div style="border-bottom:1px solid #e5e7eb;height:28px"></div>').join('');
  const notesPages = `
    <div style="page-break-after:always">
      <h1 style="text-align:center;color:#1E3A5F;font-family:'Cormorant Garamond',Georgia,serif">Notes</h1>
      ${lines}
    </div>
    <div style="page-break-after:always">
      <h1 style="text-align:center;color:#1E3A5F;font-family:'Cormorant Garamond',Georgia,serif">Notes</h1>
      ${lines}
    </div>
  `;

  // Back cover — white bg, logo with drop shadow, "Powered by Healist.AI" + tagline
  const backCover = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;text-align:center;background:white;padding:40px">
      <img src="${escapeHTML(logoUrl)}" alt="Healist.AI" style="width:120px;height:120px;filter:drop-shadow(20px 20px 12px rgba(0,0,0,0.25))" />
      <p style="color:#1A1A2E;font-size:14px;margin-top:16px;text-align:center;line-height:1.6">
        Powered by <strong>Healist.AI</strong><br/>
        Precision AI Health Technology&trade;
      </p>
    </div>
  `;

  return `<!DOCTYPE html><html>
<head>
  <title>${escapeHTML(displayName)}'s Recipe Book</title>
  <style>
    @page { margin: 0.6in; }
    body { font-family: 'Outfit', Arial, sans-serif; color: #1f2937; line-height: 1.6; margin: 0; padding: 0; }
    h1 { color: #1E3A5F; font-size: 22px; margin: 0 0 4px; }
    h2 { color: #1E3A5F; font-size: 16px; margin: 16px 0 8px; border-bottom: 2px solid #C9A962; padding-bottom: 4px; }
    p { margin: 4px 0; }
    ul, ol { padding-left: 24px; }
    li { margin-bottom: 4px; font-size: 13px; }
    .meta { color: #6b7280; font-size: 13px; }
    .brand-line { height: 3px; background: linear-gradient(90deg, #1E3A5F, #C9A962); margin: 12px 0; border-radius: 2px; }
  </style>
</head>
<body>
  ${coverPage}
  ${tocPage}
  ${recipePages}
  ${notesPages}
  ${backCover}
</body></html>`;
}

/**
 * Print a full Recipe Book PDF: cover, TOC, recipe pages, notes, back cover.
 */
export function printRecipeBook(recipes, userName, coverUrl) {
  const fullHtml = buildRecipeBookHTML(recipes, userName, coverUrl);
  if (!fullHtml) return;
  const displayName = userName || 'My';
  printHTMLDocument(fullHtml, `${displayName}'s Recipe Book`);
}

export function printProgramDetail(details) {
  const program = details?.program;
  if (!program) return;

  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

  let scheduleHtml = '';
  if (program.weekly_schedule && program.source_type === 'ai_generated') {
    const sessionDetails = program.generation_context?.ai_response?.session_details || [];

    scheduleHtml = '<h2>Weekly Workout Schedule</h2>';
    for (const dayKey of dayOrder) {
      const daySchedule = program.weekly_schedule[dayKey];
      const isRest = !daySchedule || daySchedule.type === 'rest';

      if (isRest) {
        scheduleHtml += `<div class="day-card"><div class="day-header" style="background:#F9FAFB;color:#6B7280">${dayLabels[dayKey]}</div><div class="day-content" style="color:#6B7280">Rest Day - Recovery &amp; Flexibility</div></div>`;
      } else {
        const session = sessionDetails.find(s => s.focus === daySchedule.focus);
        const headerColor = daySchedule.type === 'cardio' ? '#FEF3C7' : '#EEF2FF';
        let contentHtml = '';

        if (session) {
          if (session.warmup?.length) {
            contentHtml += `<div class="section-label">Warmup</div><p>${session.warmup.map(w => escapeHTML(w)).join(' &bull; ')}</p>`;
          }
          if (session.main_workout?.length) {
            contentHtml += '<div class="section-label">Main Workout</div>';
            for (const ex of session.main_workout) {
              const name = ex.exercise || ex.exercise_name || '';
              let detail = '';
              if (ex.sets && ex.reps) detail += `${ex.sets} × ${ex.reps}`;
              if (ex.duration_min) detail += `${ex.duration_min} min`;
              if (ex.intervals) detail += ` (${escapeHTML(ex.intervals)})`;
              if (ex.rest_sec) detail += ` • ${ex.rest_sec}s rest`;
              contentHtml += `<div class="exercise-row"><span class="exercise-name">${escapeHTML(name)}</span><span class="exercise-detail">${detail}</span></div>`;
            }
          }
          if (session.cooldown?.length) {
            contentHtml += `<div class="section-label">Cooldown</div><p>${session.cooldown.map(c => escapeHTML(c)).join(' &bull; ')}</p>`;
          }
        } else {
          contentHtml = `<p>${escapeHTML(daySchedule.type)} - ${escapeHTML((daySchedule.focus || '').replace('_', ' '))}</p>`;
        }

        const badge = `${escapeHTML(daySchedule.type)} • ${escapeHTML((daySchedule.focus || '').replace('_', ' '))} • ${daySchedule.duration_min || 60} min`;
        scheduleHtml += `<div class="day-card"><div class="day-header" style="background:${headerColor}">${dayLabels[dayKey]} <span class="meta" style="font-weight:400;margin-left:8px">${badge}</span></div><div class="day-content">${contentHtml}</div></div>`;
      }
    }
  }

  printHTML(`
    <h1>${escapeHTML(program.program_name)}</h1>
    <p class="meta">${program.program_duration_weeks || ''} weeks • ${program.sessions_per_week || ''} sessions/week • ${escapeHTML(program.fitness_level || '')}</p>
    <div class="brand-line"></div>
    ${program.program_description ? `<h2>Description</h2><p>${escapeHTML(program.program_description)}</p>` : ''}
    ${scheduleHtml}
  `, program.program_name || 'Fitness Program');
}
